// client/guest/components/HomeScreen.tsx
import React from "react";
import { EventLite, Step, fmtMoney } from "../utils";

export default function HomeScreen({
  homePassReady,
  onBuyClick,
  onPassClick,
}: {
  homePassReady: boolean;
  onBuyClick: () => void;
  onPassClick: () => void | Promise<void>;
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-3.5">
        <div className="text-[15px] sm:text-[16px] font-semibold mb-1">UralBass</div>
        <div className="text-[13px] text-white/70">
          Добро пожаловать! Выберите действие ниже.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {/* Купить билет */}
        <button
          onClick={onBuyClick}
          className="group rounded-2xl px-3 py-3 bg-white/8 hover:bg-white/12 border border-white/10 flex flex-col items-center justify-center"
          aria-label="Купить билет"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-600/90 group-hover:bg-emerald-600 text-white transition">
            {/* ticket icon */}
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M22 10.5a2.5 2.5 0 0 1-2.5 2.5V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3a2.5 2.5 0 0 1 0-5V5a2 2 0 0 1 2-2h11.5a2 2 0 0 1 2 2v3a2.5 2.5 0 0 1 2.5 2.5zM8 6h8v2H8V6zm0 4h8v2H8v-2z"/>
            </svg>
          </div>
          <div className="mt-3 text-[13px] font-semibold">Купить билет</div>
          <div className="text-[11px] text-white/70">Выбор и оплата</div>
        </button>

        {/* Мои пропуска */}
        <button
          onClick={homePassReady ? onPassClick : undefined}
          disabled={!homePassReady}
          className={`group rounded-2xl px-3 py-3 border border-white/10 flex flex-col items-center justify-center ${homePassReady ? 'bg-white/8 hover:bg-white/12' : 'bg-white/5 text-white/60 cursor-not-allowed opacity-70'}`}
          aria-label="Мои пропуска"
        >
          <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white transition ${homePassReady ? 'bg-white/15 group-hover:bg-white/20' : 'bg-white/10'}`}>
            {/* qr/pass icon */}
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v6h-2v-6zm4 4h6v2h-6v-2z"/>
            </svg>
          </div>
          <div className="mt-3 text-[13px] font-semibold">Мои пропуска</div>
          <div className={homePassReady ? "text-[11px] text-white/70" : "text-[11px] text-white/50"}>
            {homePassReady ? "Открыть список" : "Нет готовых"}
          </div>
        </button>
      </div>
    </div>
  );
}

/* --- Named exports to split GuestFlow UI blocks --- */

export function EventList({
  events,
  loading,
  error,
  onRetry,
  onSelect,
}: {
  events: EventLite[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onSelect: (id: number) => void | Promise<void>;
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="text-[13px] text-white/80">Выберите мероприятие</div>

      {error && (
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">
          {error}
          <button onClick={onRetry} className="ml-2 underline text-red-100/90 hover:text-red-50">
            Повторить
          </button>
        </div>
      )}

      {loading && (
        <div className="rounded-2xl border border-white/10 bg-white/6 p-3 text-sm text-white/80">
          Загружаем список событий…
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/6 p-3 text-sm text-white/80">
          Активных событий пока нет.
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="grid grid-cols-1 gap-2">
          {events.map((ev) => {
            const start = ev.start_at ? new Date(ev.start_at) : null;
            const end = ev.end_at ? new Date(ev.end_at) : null;
            const d2 = (d: Date | null) =>
              d
                ? `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`
                : "??.??.????";
            return (
              <button
                key={ev.id}
                onClick={() => onSelect(ev.id)}
                className="w-full text-left rounded-2xl px-3 py-2.5 bg-white/8 hover:bg-white/12 border border-white/10 transition"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate">{ev.title || `Событие #${ev.id}`}</div>
                    <div className="text-xs text-white/70 mt-0.5">
                      {`${d2(start)} — ${d2(end)}`}
                      {ev.venue ? ` • ${ev.venue}` : ""}
                    </div>
                  </div>
                  <div className="shrink-0 rounded-lg bg-white/10 text-white px-2 py-0.5 text-[11px]">Выбрать</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PassesList({
  items,
  loading,
  error,
  onRetry,
  onOpenQR,
}: {
  items: { event: EventLite; step: Step | null }[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onOpenQR: (eventId: number) => void | Promise<void>;
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="text-[13px] text-white/80">Мои пропуска</div>

      {error && (
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">
          {error}
          <button onClick={onRetry} className="ml-2 underline text-red-100/90 hover:text-red-50">
            Повторить
          </button>
        </div>
      )}

      {loading && (
        <div className="rounded-2xl border border-white/10 bg-white/6 p-3 text-sm text-white/80">
          Проверяем готовность пропусков…
        </div>
      )}

      {!loading && !error && items.filter(p => p.step === "pass" || p.step === "done").length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/6 p-3 text-sm text-white/80">
          Готовых пропусков пока нет.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 gap-2">
          {items.map(({ event: ev, step }) => {
            const ready = step === "pass" || step === "done";
            const d2 = (iso?: string | null) => {
              if (!iso) return "??.??.????";
              const d = new Date(iso);
              return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
            };
            return (
              <div
                key={ev.id}
                className={`w-full rounded-2xl px-3 py-2.5 border transition ${ready ? "bg-white/8 hover:bg-white/12 border-white/10" : "bg-white/5 border-white/10 opacity-80"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate">{ev.title || `Событие #${ev.id}`}</div>
                    <div className="text-xs text-white/70 mt-0.5">
                      {`${d2(ev.start_at)} — ${d2(ev.end_at)}`}{ev.venue ? ` • ${ev.venue}` : ""}
                    </div>
                  </div>
                  {step === "done" ? (
                    <span className="shrink-0 rounded-lg bg-emerald-500/20 text-emerald-100 px-2 py-0.5 text-[11px]">
                      Пройден
                    </span>
                  ) : ready ? (
                    <button
                      className="shrink-0 rounded-lg bg-white/10 text-white px-2 py-0.5 text-[11px]"
                      onClick={() => onOpenQR(ev.id)}
                    >
                      Открыть QR
                    </button>
                  ) : (
                    <span className="shrink-0 rounded-lg bg-white/8 text-white/70 px-2 py-0.5 text-[11px]">
                      {step === "payment" ? "Ожидает оплаты" : "Не готово"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PaymentBox({
  promoInput,
  setPromoInput,
  appliedPromo,
  promoLoading,
  promoError,
  finalAmountForPay,
  currency,
  onActivatePromo,
  onPay,
}: {
  promoInput: string;
  setPromoInput: (v: string) => void;
  appliedPromo: string | null;
  promoLoading: boolean;
  promoError: string | null;
  finalAmountForPay: number | null;
  currency: string;
  onActivatePromo: () => void | Promise<void>;
  onPay: () => void | Promise<void>;
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="text-[13px] text-white/80 mb-3">
        Оплата участия • {finalAmountForPay != null ? `${fmtMoney(finalAmountForPay)} ${currency}` : `0 ${currency}`}
      </div>

      {finalAmountForPay === 0 && (
        <div className="mb-3 rounded-lg border border-white/10 bg-amber-500/10 text-amber-200 px-3 py-2 text-sm">
          Оплата не требуется: промокод дал 100% скидку. Заявка отправлена на подтверждение администратору.
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-white/[.06] p-2.5 mb-3 overflow-visible">
        <div className="text-[13px] text-white/80 mb-2">Промокод (необязательно)</div>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="text"
            placeholder="PROMO2025"
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
            className="min-w-0 flex-1 rounded-xl bg-black/20 border border-white/10 px-2.5 py-1.5 text-white placeholder:text-white/40"
          />
          <button
            type="button"
            onClick={onActivatePromo}
            disabled={!promoInput.trim() || promoLoading}
            className="shrink-0 whitespace-nowrap rounded-xl px-3.5 py-1.5 font-semibold text-white bg-white/12 hover:bg-white/16 disabled:opacity-60"
          >
            {promoLoading ? "Проверяем…" : "Активировать"}
          </button>
        </div>

        {promoError && <div className="mt-2 text-xs text-red-200">{promoError}</div>}
        {appliedPromo && (
          <div className="mt-2 text-xs text-emerald-200">
            Промокод «{appliedPromo}» применён. Новая сумма: {finalAmountForPay != null ? `${fmtMoney(finalAmountForPay)} ${currency}` : `0 ${currency}`}
          </div>
        )}
      </div>

      <div className="flex-1" />

      <button
        type="button"
        className="w-full mt-1 rounded-2xl px-4 py-3 font-semibold text-white bg-emerald-600/95 hover:bg-emerald-500 shadow-[inset_0_1px_0_rgba(255,255,255,.08)]"
        onClick={onPay}
        disabled={finalAmountForPay == null}
      >
        Оплатить {finalAmountForPay != null ? `${fmtMoney(finalAmountForPay)} ${currency}` : `0 ${currency}`}
      </button>
    </div>
  );
}
